import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SurveyTemplate from "./SurveyTemplate";
import "../styles/components/MultiPageSurvey.css";
import { convertToMetric } from "../utils/unitConversions";

const SurveyPage2 = ({
  surveyData,
  updateSurveyData,
  onNext,
  onBack,
  onNavigationBack,
  currentSection,
  initialQuestionIndex = 0,
  updatePageState,
  pageNumber,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(initialQuestionIndex);
  const [localData, setLocalData] = useState({
    weight: surveyData.weight || "",
    height: surveyData.height || "",
    heightFeet: surveyData.heightFeet || "",
    heightInches: surveyData.heightInches || "",
    weightUnit: surveyData.weightUnit || "kg",
    heightUnit: surveyData.heightUnit || "cm",
  });

  const updatePageStateRef = useRef(updatePageState);
  updatePageStateRef.current = updatePageState;
  const lastSyncedQuestion = useRef(null);
  useEffect(() => {
    if (lastSyncedQuestion.current === currentQuestion || !updatePageStateRef.current || !pageNumber) return;
    lastSyncedQuestion.current = currentQuestion;
    updatePageStateRef.current(pageNumber, currentQuestion);
  }, [currentQuestion, pageNumber]);

  const questions = [
    {
      id: "weight",
      question: t("survey.page2.weight.question"),
      type: "number",
      placeholder: t("survey.page2.weight.placeholder"),
      unit: localData.weightUnit,
      unitLabel: t("survey.page2.weight.unitLabel"),
      units: t("survey.page2.weight.units", { returnObjects: true }),
      info: t("survey.page2.weight.info"),
    },
    {
      id: "height",
      question: t("survey.page2.height.question"),
      type: "number",
      placeholder: t("survey.page2.height.placeholder"),
      unit: localData.heightUnit,
      unitLabel: t("survey.page2.height.unitLabel"),
      units: t("survey.page2.height.units", { returnObjects: true }),
      info: t("survey.page2.height.info"),
    },
  ];

  const handleInputChange = (e) => {
    const currentQ = questions[currentQuestion];
    setLocalData((prev) => ({
      ...prev,
      [currentQ.id]: e.target.value,
    }));
  };


  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Save all data and move to next page
      Object.keys(localData).forEach((key) => {
        let value = localData[key];

        // Convert numeric fields to numbers
        if ((key === "weight" || key === "height") && value !== "") {
          value = parseFloat(value);
        }

        updateSurveyData(key, value);
      });

      // Convert measurements to metric for calculations
      if (localData.weight && localData.weightUnit) {
        const weightInKg = convertToMetric({ value: localData.weight, unit: localData.weightUnit });
        updateSurveyData("weightInKg", weightInKg);
      }

      if (localData.heightUnit === "ft") {
        if (localData.heightFeet) {
          const feet = parseInt(localData.heightFeet) || 0;
          const inches = parseInt(localData.heightInches) || 0;
          const totalInches = (feet * 12) + inches;
          const heightInCm = totalInches * 2.54;
          updateSurveyData("heightInCm", heightInCm);
          updateSurveyData("height", parseFloat(`${feet}.${inches}`));
          updateSurveyData("heightFeet", localData.heightFeet);
          updateSurveyData("heightInches", localData.heightInches);
        }
      } else if (localData.height && localData.heightUnit) {
        const heightInCm = convertToMetric({ value: localData.height, unit: localData.heightUnit });
        updateSurveyData("heightInCm", heightInCm);
      }

      onNext();
    }
  };

  const handleHeaderBack = () => {
    // Header back button goes to SurveyPage1
    navigate("/survey/1");
  };

  const handleNavigationBack = () => {
    // Navigation back button goes to previous question within the page
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      // If on first question, go to previous page
      if (onNavigationBack) {
        onNavigationBack();
      }
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered = (() => {
    if (currentQ.id === "height" && localData.heightUnit === "ft") {
      return localData.heightFeet !== "" && localData.heightFeet !== undefined;
    }
    return localData[currentQ.id] !== undefined &&
      localData[currentQ.id] !== null &&
      (currentQ.type === "number"
        ? localData[currentQ.id] !== "" && localData[currentQ.id] !== 0
        : localData[currentQ.id].toString().trim() !== "");
  })();
  const isLastQuestion = currentQuestion === questions.length - 1;

  const surveyConfig = {
    introText: t("survey.page2.intro"),
    questionText: currentQ.question,
    options: [],
    infoSections: [
      {
        title: t("survey.page2.whyThisMatters"),
        content: currentQ.info,
      },
      {
        title: t("survey.page2.noPressure"),
        content: t("survey.page2.noPressureContent"),
      },
    ],
  };

  return (
    <div className="survey-page-2">
      <SurveyTemplate
        introText={surveyConfig.introText}
        questionText={surveyConfig.questionText}
        options={surveyConfig.options}
        infoSections={surveyConfig.infoSections}
        onOptionSelect={() => {}}
        selectedOption=""
        onBack={handleHeaderBack}
        infoPanelLayout={currentQ.id === "weight" || currentQ.id === "height" ? "collapsible" : "sidepanel"}
        customInput={
          <div className="custom-input-container">
            {currentQ.id === "height" && localData.heightUnit === "ft" ? (
              <>
                <div className="feet-inches-wrapper">
                  <div className="feet-input-group">
                    <input
                      type="number"
                      value={localData.heightFeet}
                      onChange={(e) => setLocalData((prev) => ({ ...prev, heightFeet: e.target.value }))}
                      placeholder={t("survey.page2.height.feetPlaceholder", "Feet")}
                      className="integrated-input feet-input"
                      min="1"
                      max="8"
                      step="1"
                    />
                    <span className="unit-label-inline">ft</span>
                  </div>
                  <div className="inches-input-group">
                    <input
                      type="number"
                      value={localData.heightInches}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || (parseInt(val) >= 0 && parseInt(val) <= 11)) {
                          setLocalData((prev) => ({ ...prev, heightInches: val }));
                        }
                      }}
                      placeholder={t("survey.page2.height.inchesPlaceholder", "Inches")}
                      className="integrated-input inches-input"
                      min="0"
                      max="11"
                      step="1"
                    />
                    <span className="unit-label-inline">in</span>
                  </div>
                </div>
                <div className="integrated-unit-buttons" style={{ marginTop: '8px' }}>
                  {Object.entries(currentQ.units).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`integrated-unit-button ${localData.heightUnit === key ? 'active' : ''}`}
                      onClick={() => setLocalData((prev) => ({ ...prev, heightUnit: key }))}
                    >
                      {key.toUpperCase()}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="integrated-input-wrapper">
                <input
                  type="number"
                  value={localData[currentQ.id]}
                  onChange={handleInputChange}
                  placeholder={currentQ.placeholder}
                  className="integrated-input"
                  min="1"
                  max={currentQ.id === "weight" ? "500" : "300"}
                  step="1"
                />
                <div className="integrated-unit-buttons">
                  {Object.entries(currentQ.units).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`integrated-unit-button ${localData[`${currentQ.id}Unit`] === key ? 'active' : ''}`}
                      onClick={() => {
                        const unitKey = `${currentQ.id}Unit`;
                        setLocalData((prev) => ({
                          ...prev,
                          [unitKey]: key,
                        }));
                      }}
                    >
                      {key.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        }
        progressContainer={
          <div className="progress-container">
            {/* Section Progress */}
            <div className="section-progress">
              <div className="section-indicators">
                <div
                  className={`section-indicator ${
                    currentSection >= 1 ? "active" : ""
                  }`}
                >
                  <span className="section-number">1</span>
                  <span className="section-label">Demographics</span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 2 ? "active" : ""
                  }`}
                >
                  <span className="section-number">2</span>
                  <span className="section-label">Measurements</span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 3 ? "active" : ""
                  }`}
                >
                  <span className="section-number">3</span>
                  <span className="section-label">Family History</span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 4 ? "active" : ""
                  }`}
                >
                  <span className="section-number">4</span>
                  <span className="section-label">Health</span>
                </div>
                <div
                  className={`section-indicator ${
                    currentSection >= 5 ? "active" : ""
                  }`}
                >
                  <span className="section-number">5</span>
                  <span className="section-label">Lifestyle</span>
                </div>
              </div>
            </div>

            {/* Question Progress */}
            <div className="question-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                    height: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <span className="progress-text">
                {currentQuestion + 1} of {questions.length}
              </span>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button
              className="nav-button back-button"
              onClick={handleNavigationBack}
            >
              {t("common.back")}
            </button>
            <button className="nav-button next-button" onClick={handleNext} disabled={!isAnswered}>
              {isLastQuestion ? t("common.continue") : t("common.next")}
            </button>
          </div>
        }
      />
    </div>
  );
};

export default SurveyPage2;
